<script lang="ts" setup>
import LeButton from "@dsk/components/Button/LeButton.vue";
import LeIconButton from "@dsk/components/IconButton/LeIconButton.vue";
import LeTooltip from "@dsk/components/LeTooltip.vue";
import LeSelectorButton from "@dsk/components/SelectorButton/LeSelectorButton.vue";
import LeSelectorButtonGroup from "@dsk/components/SelectorButton/LeSelectorButtonGroup.vue";
import LeEmail from "@dsk/icons/LeEmail.vue";
import LeMagnifyingGlass from "@dsk/icons/LeMagnifyingGlass.vue";
import LePen from "@dsk/icons/LePen.vue";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import LeAboutDictionaryButton from "@/components/articles/LeAboutDictionaryButton.vue";
import LeNgArticleInfoBox from "@/components/articles/LeNgArticleInfoBox.vue";
import LeFreeSearchesLeftAlert from "@/components/searchbar/LeFreeSearchesLeftAlert.vue";
import { useCustomCompanySetup } from "@/composables";
import { config } from "@/config";
import { DictionariesHelper } from "@/helpers";
import { useNotesStore, useSearchStore, useUserStore } from "@/stores";
import type { Dictionary, DictionarySearchMetadata, SearchResultsTypes } from "@/types";

import LeFeedbackButton from "./LeFeedbackButton.vue";
import LeSuggestTermsButton from "./LeSuggestTermsButton.vue";

const props = defineProps<{
  resultsType: SearchResultsTypes;
  selectedDictionary?: Dictionary;
  feedbackEmail: string;
}>();

const emit = defineEmits<{
  metadataSearch: [item: DictionarySearchMetadata];
}>();

const i18n = useI18n();
const { t } = i18n;
const { isOrdbogenAccount } = config.user;

const searchStore = useSearchStore();
const notesStore = useNotesStore();

const { userData } = storeToRefs(useUserStore());
const { showSuggestTerms, suggestTermDictionaryId } = useCustomCompanySetup();

const showToolboxActions = computed<boolean>(() => {
  if (searchButtons.value.length) {
    return true;
  }

  const isExactOrUpsell = isExact.value || isUpsell.value;
  const hasToolboxButtons = props.feedbackEmail !== "" || hasAboutDictionary.value || isOrdbogenAccount;

  return isExactOrUpsell && hasToolboxButtons;
});

const searchButtons = computed<DictionarySearchMetadata[]>(
  () => props.selectedDictionary?.metadata.filter((metadata) => !metadata.isSpecialSearch) ?? []
);

const activeSearchButton = computed<DictionarySearchMetadata | null>(() => {
  if (!searchButtons.value) {
    return null;
  }

  const selectedDictionaryMetadata = searchStore.selectedDictionaryMetadata;
  if (!selectedDictionaryMetadata) {
    return null;
  }

  const selectedSearchButton = searchButtons.value.find((searchButton) =>
    selectedDictionaryMetadata.includes(searchButton.name)
  );
  if (selectedSearchButton) {
    return selectedSearchButton;
  }

  const defaultSearchButton = searchButtons.value.find((searchButton) => searchButton.isStatic);
  if (defaultSearchButton) {
    return defaultSearchButton;
  }

  return null;
});

const activeSearchButtonIndex = computed<number | undefined>(() =>
  searchButtons.value.findIndex((sb) => sb.name === activeSearchButton.value?.name)
);

const specialSearchButtons = computed<DictionarySearchMetadata[]>(
  () => props.selectedDictionary?.metadata.filter((metadata) => metadata.isSpecialSearch) ?? []
);

const hasAboutDictionary = computed<boolean>(
  () => props.selectedDictionary !== undefined && DictionariesHelper.hasAboutDictionary(props.selectedDictionary)
);

const isExact = computed<boolean>(() => props.resultsType === "exact");

const isUpsell = computed<boolean>(() => props.selectedDictionary?.isUpsell ?? false);

const showSpecialSearchButtonsFirst = computed(() => searchButtons.value.length === 0);
</script>

<template>
  <section class="le-search-toolbox">
    <LeFreeSearchesLeftAlert :free-searches-left="userData.freeSearchesLeft" />

    <div
      v-if="showToolboxActions"
      class="le-search-toolbox__actions"
      :class="{ 'le-search-toolbox__actions--last': showSpecialSearchButtonsFirst }"
    >
      <LeSelectorButtonGroup
        class="le-search-toolbox__metadata-search-btn-group"
        :items="searchButtons"
        :selected="activeSearchButtonIndex"
        @input="emit('metadataSearch', $event.item)"
      >
        <template #default="{ state, handleBlur, handleFocusNext, handleFocusPrev, handleSelect }">
          <LeSelectorButton
            v-for="(searchButton, index) in searchButtons"
            :key="searchButton.name"
            :index="index"
            :tabbable="state.tabbableIndex === index"
            :selected="state.selectedIndex === index"
            class="le-search-toolbox__search-btn"
            size="small"
            @blur="handleBlur()"
            @select="handleSelect($event)"
            @focus-next="handleFocusNext($event)"
            @focus-prev="handleFocusPrev($event)"
            >{{ searchButton.title }}</LeSelectorButton
          >
        </template>

        <template #after>
          <div v-if="isExact || isUpsell" class="le-search-toolbox__action-icons">
            <LeTooltip
              v-if="isExact && props.feedbackEmail"
              :text="t('le-search-toolbox.feedback-icon-tooltip')"
              :margin-bottom="10"
            >
              <LeIconButton
                tag="a"
                :href="`mailto:${props.feedbackEmail}`"
                size="l"
                :aria-label="t('le-search-toolbox.feedback-icon-tooltip')"
              >
                <LeEmail role="presentation" width="16px" height="16px" />
              </LeIconButton>
            </LeTooltip>

            <LeAboutDictionaryButton
              v-if="props.selectedDictionary"
              :dictionary="props.selectedDictionary"
              :tooltip="t('le-dictionary-info-button.tooltip')"
            />

            <LeFeedbackButton v-if="isExact" />

            <LeTooltip v-if="isExact && isOrdbogenAccount" :text="t('le-note.my-notes')" :margin-bottom="10">
              <LeIconButton color="primary" size="l" :aria-label="t('le-note.my-notes')" @click="notesStore.toggle()">
                <LePen role="presentation" />
              </LeIconButton>
            </LeTooltip>
          </div>
        </template>
      </LeSelectorButtonGroup>
    </div>

    <template v-if="props.selectedDictionary?.id === 'cent-concepts-dada'">
      <LeNgArticleInfoBox
        html="Søg med enkeltord eller med flere ved at skrive + eller - direkte foran søgeord."
      ></LeNgArticleInfoBox>
    </template>

    <div v-if="showSuggestTerms || specialSearchButtons.length" class="le-search-toolbox__search-actions">
      <div class="le-search-toolbox__grid-wrapper">
        <!-- When empty, grid wrapper still ensures to force specialSearchButtons to the right -->
        <LeSuggestTermsButton v-if="showSuggestTerms" :dictionary-id="suggestTermDictionaryId ?? ''" />
      </div>

      <div v-if="specialSearchButtons.length" class="le-search-toolbox__grid-wrapper">
        <LeTooltip
          v-for="searchButton in specialSearchButtons"
          :key="searchButton.name"
          :text="searchButton.information"
          :margin-bottom="10"
          class="w-100"
          trigger-container-class="w-100"
        >
          <LeButton variant="outline" class="w-100" @click="emit('metadataSearch', searchButton)">
            <span class="le-search-toolbox__special-search-btn-inner">
              <LeMagnifyingGlass role="presentation" />
              <span>{{ searchButton.title }}</span>
            </span>
          </LeButton>
        </LeTooltip>
      </div>
    </div>
  </section>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";

.le-search-toolbox {
  display: grid;
  gap: 20px;

  &__actions {
    display: flex;
    flex-direction: column-reverse;
    gap: 12px;
    width: 100%;

    &--last {
      order: 1;
    }

    #media-breakpoint-up(md, {
      flex-direction: row;
      width: auto;
    });

    &--multi-row {
      flex-direction: column;
    }
  }

  &__action-icons {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;

    #media-breakpoint-up(md, {
      flex-wrap: nowrap;
    });
  }

  &__search-btn + &__action-icons {
    margin-left: 6px;
  }

  &__search-actions {
    display: flex;
    flex-direction: column;
    gap: 20px;
    align-items: flex-start;
    justify-content: space-between;

    #media-breakpoint-up(md, {
      flex-direction: row;
      gap: initial;
    });
  }

  &__grid-wrapper {
    display: grid;
    width: 100%;

    #media-breakpoint-up(md, {
      display: initial;
      width: auto;
    });
  }

  &__metadata-search-btn-group {
    display: inline-flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 6px;

    #media-breakpoint-up(md, {
      flex-direction: row;
    });
  }

  &__special-search-btn-inner {
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 8px;
  }
}
</style>
